import { type SectionPlane, type Viewer } from '@xeokit/xeokit-sdk';
import { math } from '@xeokit/xeokit-sdk/dist/xeokit-sdk.es.js';
import { buildRvtSectionBoxes } from '~/utils/viewer/explorer/rvt-tree';
import type {
  ExplorerSectionBox,
  ExplorerSectionPlane,
  ModelMetadata,
} from '~/utils/viewer/explorer/types';
import { HitHelper } from '~/utils/viewer/tools/sections-helper';

const SECTION_BOX_PLANE_ID_PREFIX = 'rvt-section-box-plane-';

const isSectionBoxPlaneId = (id: string) => String(id).startsWith(SECTION_BOX_PLANE_ID_PREFIX);

export const useViewerSectionsStore = defineStore('viewer-sections-store', () => {
  const { $SectionPlanesPlugin } = useNuxtApp();
  const viewerStore = useViewerStore();
  const toolbarStore = useToolbarStore();
  const isInitialized = ref(false);
  const numberOfSections = ref(0);
  const isBetweenDragAndClick = ref(false);
  const editedSectionPlaneId = ref<string>();
  const activeSectionBoxId = ref<string>();
  let sectionPlanes: SectionPlane[] = [];
  let sectionBoxPlanes: SectionPlane[] = [];
  let viewer: Viewer;
  let hitHelper: HitHelper;
  const tempAABB = math.AABB3();
  const tempVec3 = math.vec3();

  let plugin: InstanceType<typeof $SectionPlanesPlugin> | undefined;

  watch(viewerStore, () => {
    if (!viewer && viewerStore.getHelpers().viewer) {
      viewer = viewerStore.getHelpers().viewer as Viewer;
      hitHelper = new HitHelper(viewer);
    }
  });

  let lastDist = -1;
  const pos = math.vec3();

  function calculateScale() {
    const dist = Math.abs(math.distVec3(viewer.camera.eye, pos));

    if (viewer.camera.projection === 'perspective') {
      if (dist !== lastDist) {
        hitHelper.setRootNodeScale(
          0.07 * dist * Math.tan(viewer.camera.perspective.fov * math.DEGTORAD),
        );
      }
    } else if (viewer.camera.projection === 'ortho') {
      hitHelper.setRootNodeScale(viewer.camera.ortho.scale / 10);
    }
    lastDist = dist;
  }

  function helperOnSceneTick(viewer: Viewer) {
    return viewer.scene.on('tick', calculateScale);
  }

  function hideHelper() {
    if (!hitHelper) return;
    hitHelper.hide();
  }

  function handleMouseMove(coords: number[]) {
    const hit = viewer.scene.pick({
      canvasPos: coords,
      pickSurface: true,
    });

    if (!hitHelper) return;

    if (hit) {
      hitHelper.show(hit);
      calculateScale();
    } else {
      hitHelper.hide();
    }
  }

  function getPlugin() {
    return plugin;
  }

  function getSectionPlanes() {
    const plugin = getPlugin();
    if (!plugin) return;

    return Object.values(plugin.sectionPlanes).filter((plane) => !isSectionBoxPlaneId(plane.id));
  }

  function getSectionBoxPlanes() {
    const plugin = getPlugin();
    if (!plugin) return [];

    if (sectionBoxPlanes.length === 0) {
      sectionBoxPlanes = Array.from({ length: 6 }, (_, index) => {
        const plane = plugin.createSectionPlane({ id: `${SECTION_BOX_PLANE_ID_PREFIX}${index}` });
        plane.active = false;
        return plane;
      });
    }

    return sectionBoxPlanes;
  }

  function getSectionBoxes(): ExplorerSectionBox[] {
    const viewer = viewerStore.getHelpers().viewer;
    if (!viewer) return [];

    return Object.values(viewer.scene.models).flatMap((model) => {
      const sceneModel = model as unknown as {
        metadata?: ModelMetadata;
        worldMatrix?: number[];
      };

      const elements = sceneModel.metadata?.Elements;
      if (!Array.isArray(elements)) return [];

      return buildRvtSectionBoxes(elements, sceneModel.worldMatrix).map((box) => ({
        ...box,
        id: `${model.id}:${box.id}`,
      }));
    });
  }

  function applySectionBox(planes: ExplorerSectionPlane[]) {
    const boxPlanes = getSectionBoxPlanes();
    if (boxPlanes.length !== planes.length) return;

    planes.forEach((plane, index) => {
      boxPlanes[index].pos = plane.pos;
      boxPlanes[index].dir = plane.dir;
      boxPlanes[index].active = true;
    });

    updateSectionPlanes();
  }

  function clearSectionBox() {
    sectionBoxPlanes.forEach((plane) => (plane.active = false));
    activeSectionBoxId.value = undefined;
    updateSectionPlanes();
  }

  function toggleSectionBox(box: ExplorerSectionBox) {
    if (activeSectionBoxId.value === box.id) {
      clearSectionBox();
      return;
    }

    applySectionBox(box.planes);
    activeSectionBoxId.value = box.id;
  }

  function init() {
    const viewer = viewerStore.getHelpers().viewer as Viewer;

    const _onSceneSectionPlaneCreated = viewer.scene.on('sectionPlaneCreated', () => {
      updateSectionPlanes();
    });

    const _onSceneSectionPlaneDestroyed = viewer.scene.on('sectionPlaneDestroyed', () => {
      updateSectionPlanes();
    });

    if (!viewer) return;

    plugin = new $SectionPlanesPlugin(viewer, {
      // overviewCanvasId: 'xeokit-section-planes-overview-canvas',
      overviewVisible: false,
    });

    initSectionMode();

    isInitialized.value = true;
  }

  function initSectionMode() {
    const canvasWrapperElement = document.getElementById('xeokit-wrapper') as HTMLElement;

    canvasWrapperElement.addEventListener('click', (e) => {
      if (e.button === 0) {
        if (!toolbarStore.sliceObjectsMode.actionActive) return;

        const viewer = viewerStore.getHelpers().viewer as Viewer;
        if (!viewer) return;

        const coords = getMouseCanvasPos(e);

        const pickResult = viewer.scene.pick({
          canvasPos: coords,
          pickSurface: true, // <<------ This causes picking to find the intersection point on the entity
        });

        const isPotreePick = !!pickResult?.entity && viewerStore.isPotreeModel(pickResult.entity);

        if (pickResult && pickResult.entity && (pickResult.entity.isObject || isPotreePick)) {
          // Slice only model objects, not 3D UI helpers

          const plugin = getPlugin();
          if (!plugin) return;

          if (!isNaN(pickResult.worldNormal[0])) {
            const sectionPlane = plugin.createSectionPlane({
              pos: pickResult.worldPos,
              dir: math.mulVec3Scalar(pickResult.worldNormal, -1),
            });

            plugin.showControl(sectionPlane.id);

            editedSectionPlaneId.value = sectionPlane.id;
            toolbarStore.sliceObjectsMode.actionActive = false;
          } else {
            const lasPickResult = viewer.scene.pick({
              canvasPos: coords,
            });

            if (lasPickResult && lasPickResult.entity) {
              const camera = viewer.camera;
              const aabb = lasPickResult.entity.aabb;

              const pickedPos = pickResult.worldPos ?? lasPickResult.worldPos;
              const center =
                isPotreePick && pickedPos
                  ? Array.from(pickedPos)
                  : [
                      (aabb[0] + aabb[3]) * 0.5,
                      (aabb[1] + aabb[4]) * 0.5,
                      (aabb[2] + aabb[5]) * 0.5,
                    ];

              const dir = {
                x: camera.look[0] - camera.eye[0],
                y: camera.look[1] - camera.eye[1],
                z: camera.look[2] - camera.eye[2],
              };

              const length = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z);
              dir.x /= length;
              dir.y /= length;
              dir.z /= length;

              // Determine vertical angle based on worldUp
              const verticalAngle = Math.acos(dir.y);
              const threshold = Math.PI / 4;
              let planeDir;

              if (verticalAngle < threshold) planeDir = [0, 1, 0];
              else if (verticalAngle < 3 * threshold) planeDir = [dir.x, 0, dir.z];
              else planeDir = [0, -1, 0];

              const sectionPlane = plugin.createSectionPlane({
                pos: center,
                dir: math.normalizeVec3(planeDir),
              });

              plugin.showControl(sectionPlane.id);

              editedSectionPlaneId.value = sectionPlane.id;
              toolbarStore.sliceObjectsMode.actionActive = false;
            }
          }
        }
      }
    });

    updateSectionPlanes();
  }

  function getNumSections(): number {
    const viewer = viewerStore.getHelpers().viewer as Viewer;
    if (!viewer) {
      return 0;
    } else {
      return Object.keys(viewer.scene.sectionPlanes).filter((id) => !isSectionBoxPlaneId(id))
        .length;
    }
  }

  function clearSections() {
    const plugin = getPlugin();
    if (!plugin) return;

    Object.keys(plugin.sectionPlanes)
      .filter((id) => !isSectionBoxPlaneId(id))
      .forEach((id) => plugin.destroySectionPlane(id));

    updateSectionPlanes();
  }

  function flipSections() {
    getSectionPlanes()?.forEach((sectionPlane) => sectionPlane.flipDir());
  }

  function enableSections() {
    const viewer = viewerStore.getHelpers().viewer as Viewer;

    const sectionPlanes = viewer.scene.sectionPlanes;
    for (const id in sectionPlanes) {
      if (isSectionBoxPlaneId(id)) continue;
      const sectionPlane = sectionPlanes[id];
      sectionPlane.active = true;
    }
  }

  function disableSections() {
    const viewer = viewerStore.getHelpers().viewer as Viewer;

    const sectionPlanes = viewer.scene.sectionPlanes;
    for (const id in sectionPlanes) {
      if (isSectionBoxPlaneId(id)) continue;
      const sectionPlane = sectionPlanes[id];
      sectionPlane.active = false;
    }
    editedSectionPlaneId.value = undefined;
    hideControl();
  }

  function hideControl() {
    const plugin = getPlugin();
    if (!plugin) return;
    plugin.hideControl();
  }

  function updateSectionPlanes() {
    const plugin = getPlugin();
    if (!plugin) return;

    sectionPlanes = getSectionPlanes() ?? [];
    numberOfSections.value = getNumSections();
  }

  function editSectionPlane(sectionPlane) {
    const plugin = getPlugin();
    if (!plugin) return;

    const viewer = viewerStore.getHelpers().viewer;
    if (!viewer) return;

    plugin.hideControl();
    plugin.showControl(sectionPlane.id);

    editedSectionPlaneId.value = sectionPlane.id;

    const sectionPlanePos = sectionPlane.pos;
    tempAABB.set(viewer.scene.aabb);
    math.getAABB3Center(tempAABB, tempVec3);
    tempAABB[0] += sectionPlanePos[0] - tempVec3[0];
    tempAABB[1] += sectionPlanePos[1] - tempVec3[1];
    tempAABB[2] += sectionPlanePos[2] - tempVec3[2];
    tempAABB[3] += sectionPlanePos[0] - tempVec3[0];
    tempAABB[4] += sectionPlanePos[1] - tempVec3[1];
    tempAABB[5] += sectionPlanePos[2] - tempVec3[2];

    viewer.cameraFlight.flyTo({
      aabb: tempAABB,
      fitFOV: 65,
    });
  }

  function getMouseCanvasPos(event) {
    if (!event) {
      event = window.event;
      return [event.x, event.y];
    } else {
      let element = event.target;
      let totalOffsetLeft = 0;
      let totalOffsetTop = 0;
      while (element.offsetParent) {
        totalOffsetLeft += element.offsetLeft;
        totalOffsetTop += element.offsetTop;
        element = element.offsetParent;
      }
      return [event.pageX - totalOffsetLeft, event.pageY - totalOffsetTop];
    }
  }

  function contextMenuOnFocus(sectionPlane: SectionPlane) {
    const plugin = getPlugin();

    plugin?.hideControl();
    plugin?.showControl(sectionPlane.id);
  }

  function contextMenuOnBlur(sectionPlane: SectionPlane) {
    const plugin = getPlugin();
    if (editedSectionPlaneId.value) {
      if (sectionPlane.id === editedSectionPlaneId.value) {
        plugin?.showControl(sectionPlane.id);
      } else {
        plugin?.showControl(editedSectionPlaneId.value);
      }
    } else {
      hideControl();
    }
  }

  return {
    editedSectionPlaneId,
    numberOfSections,
    clearSections,
    flipSections,
    enableSections,
    disableSections,
    applySectionBox,
    clearSectionBox,
    toggleSectionBox,
    getSectionBoxes,
    activeSectionBoxId,
    hideControl,
    handleMouseMove,
    hideHelper,
    helperOnSceneTick,
    isInitialized,
    getPlugin,
    init,
    editSectionPlane,
    sectionPlanes,
    getSectionPlanes,
    contextMenuOnFocus,
    contextMenuOnBlur,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useViewerSectionsStore, import.meta.hot));
}
